import { Debouncer } from "@tanstack/react-pacer";

export interface StateStorage<R = unknown> {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => R;
  removeItem: (name: string) => R;
}

export interface DeferredStorage<TValue, TStored = string> {
  getItem: (name: string) => TStored | null | Promise<TStored | null>;
  setItem: (name: string, value: TValue) => void;
  removeItem: (name: string) => void;
  flush: () => void;
}

const isQuotaExceeded = (error: unknown) =>
  error instanceof Error &&
  (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED");

/**
 * The only write into browser storage. What it keeps is a convenience, so a full origin never
 * fails the app: the write is skipped. Returns whether the value landed. Other errors (storage
 * blocked by policy) stay with the caller.
 */
export function writeBrowserStorage(
  storage: Pick<Storage, "setItem">,
  key: string,
  value: string,
): boolean {
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaExceeded(error)) throw error;
    console.warn(`Browser storage is full; ${key} was not saved.`);
    return false;
  }
}

export function createMemoryStorage(): StateStorage {
  const store = new Map<string, string>();
  return {
    getItem: (name) => store.get(name) ?? null,
    setItem: (name, value) => {
      store.set(name, value);
    },
    removeItem: (name) => {
      store.delete(name);
    },
  };
}

function isStateStorage(
  storage: Partial<StateStorage> | null | undefined,
): storage is StateStorage {
  return (
    storage !== null &&
    storage !== undefined &&
    typeof storage.getItem === "function" &&
    typeof storage.setItem === "function" &&
    typeof storage.removeItem === "function"
  );
}

export function resolveStorage(storage: Partial<StateStorage> | null | undefined): StateStorage {
  return isStateStorage(storage) ? storage : createMemoryStorage();
}

/** Keep the latest value and serialize it when the debounce fires or `flush` runs. */
export function createDeferredStorage<TValue, TStored = string>(
  baseStorage: {
    getItem: (name: string) => TStored | null | Promise<TStored | null>;
    setItem: (name: string, value: TStored) => unknown;
    removeItem: (name: string) => unknown;
  },
  serialize: (value: TValue) => TStored,
  debounceMs: number = 300,
): DeferredStorage<TValue, TStored> {
  const debouncedSetItem = new Debouncer(
    (name: string, value: TValue) => {
      baseStorage.setItem(name, serialize(value));
    },
    { wait: debounceMs },
  );

  return {
    getItem: (name) => baseStorage.getItem(name),
    setItem: (name, value) => {
      debouncedSetItem.maybeExecute(name, value);
    },
    removeItem: (name) => {
      debouncedSetItem.cancel();
      // cancel() leaves the captured value in Pacer's lastArgs.
      debouncedSetItem.reset();
      baseStorage.removeItem(name);
    },
    flush: () => {
      debouncedSetItem.flush();
    },
  };
}
